import { Box } from "@ui/box/Box";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const HoverTooltip = ({
	children,
	content,
	align = "center",
	style,
	shouldShow,
	tooltipStyle,
}: {
	children: React.ReactNode;
	content: React.ReactNode;
	align?: "center" | "end";
	style?: React.CSSProperties;
	shouldShow?: (el: HTMLDivElement) => boolean;
	tooltipStyle?: React.CSSProperties;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const tooltipNode = useRef<HTMLDivElement | null>(null);
	const [show, setShow] = useState(false);
	const [pos, setPos] = useState<{ top: number; bottom: number; left: number } | null>(null);
	const [flipped, setFlipped] = useState(false);
	const [shiftX, setShiftX] = useState(0);
	const isEnd = align === "end";

	const handleMouseEnter = () => {
		if (!ref.current) return;
		if (shouldShow && !shouldShow(ref.current)) return;
		const rect = ref.current.getBoundingClientRect();
		setPos({ top: rect.top, bottom: rect.bottom, left: isEnd ? rect.right : rect.left + rect.width / 2 });
		setFlipped(false);
		setShiftX(0);
		setShow(true);
	};

	// One-shot post-mount measurement: flip vertically if the tooltip would clip the
	// top of the viewport, and shift horizontally if it would clip the left/right edge.
	// Deps are deliberately ONLY [show, pos] — the state we set inside (flipped, shiftX)
	// must NOT re-trigger us, or we get oscillation when the tooltip is wider than the
	// viewport (left-fix → overflows right → right-fix → overflows left → ...).
	useLayoutEffect(() => {
		if (!show || !pos || !tooltipNode.current) return;
		const rect = tooltipNode.current.getBoundingClientRect();
		if (rect.top < 0) setFlipped(true);
		const margin = 8;
		const vw = window.innerWidth;
		let desired = 0;
		if (rect.left < margin) desired = margin - rect.left;
		else if (rect.right > vw - margin) desired = vw - margin - rect.right;
		if (Math.abs(desired) > 0.5) setShiftX(desired);
	}, [show, pos]);

	return (
		<div ref={ref} style={style} onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)}>
			{children}
			{show &&
				pos &&
				createPortal(
					<div
						ref={tooltipNode}
						style={{
							position: "fixed",
							top: flipped ? pos.bottom : pos.top,
							left: pos.left + shiftX,
							transform: flipped
								? isEnd
									? "translate(-100%, 8px)"
									: "translate(-50%, 8px)"
								: isEnd
									? "translate(-100%, -100%) translateY(-8px)"
									: "translate(-50%, -100%) translateY(-8px)",
							zIndex: 10000,
							backgroundColor: "rgba(0, 0, 0, 0.88)",
							borderRadius: "8px",
							padding: "10px 14px",
							boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
							pointerEvents: "none",
							...tooltipStyle,
						}}>
						{content}
						<div
							style={{
								position: "absolute",
								...(flipped
									? {
											top: "-6px",
											...(isEnd
												? { right: `${16 + shiftX}px` }
												: { left: `calc(50% - ${shiftX}px)`, transform: "translateX(-50%)" }),
											width: 0,
											height: 0,
											borderLeft: "6px solid transparent",
											borderRight: "6px solid transparent",
											borderBottom: "6px solid rgba(0, 0, 0, 0.88)",
										}
									: {
											bottom: "-6px",
											...(isEnd
												? { right: `${16 + shiftX}px` }
												: { left: `calc(50% - ${shiftX}px)`, transform: "translateX(-50%)" }),
											width: 0,
											height: 0,
											borderLeft: "6px solid transparent",
											borderRight: "6px solid transparent",
											borderTop: "6px solid rgba(0, 0, 0, 0.88)",
										}),
							}}
						/>
					</div>,
					document.body,
				)}
		</div>
	);
};

export const FailReasonHint = () => (
	<Box
		sx={{
			width: "18px",
			height: "18px",
			borderRadius: "50%",
			backgroundColor: "rgba(220,38,38,0.15)",
			color: "#DC2626",
			fontSize: "12px",
			fontWeight: 700,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			cursor: "help",
			flexShrink: 0,
		}}>
		?
	</Box>
);
